// frontend/src/modules/folha/pages/AdiantamentoDetalhes.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPrint, FaFilePdf } from 'react-icons/fa';
import HoleriteHeader from '../components/HoleriteHeader';
import HoleriteFooter from '../components/HoleriteFooter';

function AdiantamentoDetalhes({ usuario, isDark = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adiantamento, setAdiantamento] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');

  // VERIFICAR SE O ID EXISTE
  useEffect(() => {
    console.log('📌 ID do adiantamento recebido:', id); // Debug
    
    if (!id) {
      setError('ID do adiantamento não fornecido');
      setLoading(false);
      return;
    }
    
    fetchAdiantamento();
  }, [id]);

  const fetchAdiantamento = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log(`🔍 Buscando adiantamento ID: ${id}`);
      
      const response = await fetch(`/api/adiantamento/${id}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Adiantamento não encontrado');
        }
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Adiantamento encontrado:', data);
      setAdiantamento(data);
      
      if (data.empresa_id) {
        const empresaResponse = await fetch(`/api/empresas/${data.empresa_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (empresaResponse.ok) {
          const empresaData = await empresaResponse.json();
          setEmpresa(empresaData);
        }
      }
    } catch (err) {
      console.error('❌ Erro:', err);
      setError(err.message || 'Erro ao buscar adiantamento');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Estilos para modo escuro/claro
  const colors = {
    background: isDark ? '#121212' : '#f8f9fa',
    card: isDark ? '#1e1e1e' : '#ffffff',
    text: isDark ? '#e9ecef' : '#212529',
    border: isDark ? '#333333' : '#dee2e6',
    textSecondary: isDark ? '#adb5bd' : '#6c757d'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>
        🔄 Carregando...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        ❌ {error}
        <br />
        <button 
          onClick={() => navigate(-1)}
          style={{
            marginTop: '15px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Voltar
        </button>
      </div>
    );
  }

  if (!adiantamento) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: colors.text }}>
        Adiantamento não encontrado
        <br />
        <button 
          onClick={() => navigate(-1)}
          style={{
            marginTop: '15px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '800px', 
      margin: '0 auto',
      backgroundColor: colors.background,
      minHeight: '100vh'
    }}>
      {/* Botões de Ação */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <button 
          onClick={() => navigate(-1)} 
          style={{ 
            background: 'none', 
            border: 'none', 
            color: '#007bff', 
            cursor: 'pointer', 
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FaArrowLeft /> Voltar
        </button>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={handlePrint} 
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaPrint /> Imprimir
          </button>
        </div>
      </div>

      {/* Adiantamento */}
      <div 
        style={{ 
          backgroundColor: colors.card, 
          padding: '30px', 
          borderRadius: '8px', 
          boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
          border: isDark ? `1px solid ${colors.border}` : 'none',
          color: colors.text
        }} 
        id="adiantamento"
      >
        {/* Cabeçalho */}
        <HoleriteHeader
          empresa={empresa || adiantamento.empresa}
          funcionario={adiantamento.funcionario}
          mesReferencia={adiantamento.mes_referencia}
          anoReferencia={adiantamento.ano_referencia}
          tipo="adiantamento"
        />

        {/* Detalhes do Adiantamento */}
        <div style={{ marginBottom: '20px' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            fontSize: '14px',
            color: colors.text
          }}>
            <tbody>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px', fontWeight: 'bold' }}>Valor Solicitado</td>
                <td style={{ padding: '10px', textAlign: 'right', fontSize: '18px', color: '#007bff' }}>
                  {formatCurrency(adiantamento.valor_solicitado)}
                </td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px', fontWeight: 'bold' }}>Valor Aprovado</td>
                <td style={{ padding: '10px', textAlign: 'right', fontSize: '18px', color: '#28a745' }}>
                  {formatCurrency(adiantamento.valor_aprovado || adiantamento.valor_solicitado)}
                </td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px' }}>Data Solicitação</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  {formatDate(adiantamento.data_solicitacao)}
                </td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px' }}>Data Pagamento</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  {formatDate(adiantamento.data_pagamento)}
                </td>
              </tr>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px' }}>Status</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    backgroundColor: adiantamento.status === 'APROVADO' ? '#d4edda' :
                                  adiantamento.status === 'REPROVADO' ? '#f8d7da' : '#fff3cd',
                    color: adiantamento.status === 'APROVADO' ? '#155724' :
                           adiantamento.status === 'REPROVADO' ? '#721c24' : '#856404'
                  }}>
                    {adiantamento.status || 'PENDENTE'}
                  </span>
                </td>
              </tr>
              {adiantamento.observacoes && (
                <tr>
                  <td style={{ padding: '10px' }}>Observações</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    {adiantamento.observacoes}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Resumo */}
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: isDark ? '#1a2a4a' : '#e3f2fd',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: `2px solid #007bff`,
          color: colors.text
        }}>
          <div>
            <strong style={{ fontSize: '16px' }}>💰 Valor do Adiantamento</strong>
            <p style={{ margin: '0', fontSize: '12px', color: colors.textSecondary }}>
              Será descontado no próximo holerite
            </p>
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
            {formatCurrency(adiantamento.valor_aprovado || adiantamento.valor_solicitado)}
          </div>
        </div>

        {/* Rodapé */}
        <HoleriteFooter
          empresa={empresa || adiantamento.empresa}
          dataPagamento={formatDate(adiantamento.data_pagamento)}
          status={adiantamento.status}
        />
      </div>

      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #adiantamento, #adiantamento * { visibility: visible; }
            #adiantamento {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
              box-shadow: none !important;
            }
            .no-print { display: none !important; }
            button { display: none !important; }
          }
        `}
      </style>
    </div>
  );
}

export default AdiantamentoDetalhes;